import React, { useEffect, useMemo, useState } from "react";
import { GeneratedData, STATUS_SAVING, STATUS_UNSAVED, ValidationRecordType, useDataGeneration } from "../../api/generate/store";
import DataTable from "../../components/DataTable";
import ProcessBar from "../../components/ProcessBar";
import { Form, Nav, Pagination, Stack } from "react-bootstrap";
import useUXStore from "../../api/ux/store";
import { useFilenameConfigDialog } from "../../components/FilenameConfigDialog";
import BasePage from "../../components/BasePage";
import Label from "../../components/Label";
import ValidationPanel, { ValidationGroup } from "../../components/ValidationPanel";
import { buildProoflistFilename } from "../../utils/functions";

type DataViewProps = {
    generated: GeneratedData
    status: string
    countsPerPage?: number
}

const TAB_ICONS: Record<string, JSX.Element> = {
    Individuals: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
            <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
            <path d="M3 13c0-2.209 2.239-4 5-4s5 1.791 5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    Companies: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
            <rect x="2.5" y="3" width="5" height="10" rx="0.75" stroke="currentColor" strokeWidth="1.5" />
            <rect x="8.5" y="1.5" width="5" height="11.5" rx="0.75" stroke="currentColor" strokeWidth="1.5" />
            <path d="M4.75 6.5h0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M10.75 5h0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M10.75 8h0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    Installments: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
            <rect x="2.5" y="3" width="11" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 6.5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M5 9h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M5 11.5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    NonInstallments: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
            <rect x="2.5" y="3" width="11" height="10" rx="1" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 6.5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M5 9h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M5 11.5h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
    ),
    Validation: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
            <path d="M3.5 2.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M3.5 5.5h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M3.5 8.5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <path d="M11.5 8l-2 2 2 2 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
}

const CONTRACT_STATUS_FILTERS = [
    { value: "AC", label: "Active (AC)" },
    { value: "CL", label: "Closed (CL)" },
    { value: "CA", label: "Closed in Advance (CA)" },
]

const DEFAULT_INCLUDED_CONTRACT_STATUSES = CONTRACT_STATUS_FILTERS.map(option => option.value)

const DataViewPage:React.FC<DataViewProps> = ({generated, countsPerPage = 50, status}) => {
    const { downloadGenerated, cutoff, discardUnsaved, unfocus, saveGenerated, isSaving, isLoadingMeta, summary } = useDataGeneration( state => state)
    const { addToast, refreshDialog } = useUXStore(state => state)
    const { showFilenameDialog } = useFilenameConfigDialog()
    const [downloading, setDownloading] = useState(false)
    const [submissionType, setSubmissionType] = useState<number>(0)
    const [includedStatuses, setIncludedStatuses] = useState<string[]>([...DEFAULT_INCLUDED_CONTRACT_STATUSES])
    const excludedStatuses = useMemo(
        () => DEFAULT_INCLUDED_CONTRACT_STATUSES.filter(status => !includedStatuses.includes(status)),
        [includedStatuses]
    )

    const [active, setActive] = useState(0)
    const [selected, setSelected] = useState("Individuals")
    const [validationPage, setValidationPage] = useState(0)
    const [validationFilter, setValidationFilter] = useState<"All" | ValidationRecordType>("All")
    const toggleStatusInclusion = (statusCode: string) => {
        setIncludedStatuses(prev => {
            if (prev.includes(statusCode)) {
                return prev.filter(code => code !== statusCode)
            }
            return [...prev, statusCode]
        })
    }

    const validationPageSize = 20

    const validationResults = useMemo(
        () => generated.Meta?.validation ?? [],
        [generated.Meta?.validation]
    )
const filteredInstallmentProviders = useMemo(() => {
        const set = new Set<string>();
        const installments = generated?.Installments ?? [];
        installments.forEach(inst => {
            const phase =
                (inst as any)?.ContractPhase ??
                (inst as any)?.contractPhase ??
                (inst as any)?.['-ContractPhase'] ??
                (inst as any)?.CONTRACTPHASE;

            if (phase && !includedStatuses.includes(phase)) return;

            const id =
                (inst as any)?.ProviderSubjectNo ??
                (inst as any)?.ProviderSubjectNumber ??
                (inst as any)?.['-ProviderSubjectNo'] ??
                (inst as any)?.['-ProviderSubjectNumber'] ??
                (inst as any)?.PROVIDERSUBJECTNO;
            if (id != null) set.add(String(id));
        });
        return set;
    }, [generated?.Installments, includedStatuses]);

    const filteredNonInstallmentProviders = useMemo(() => {
        const set = new Set<string>();
        const nonInstallments = generated?.NonInstallments ?? [];
        nonInstallments.forEach(inst => {
            const phase =
                (inst as any)?.ContractPhase ??
                (inst as any)?.contractPhase ??
                (inst as any)?.['-ContractPhase'] ??
                (inst as any)?.CONTRACTPHASE;

            if (phase && !includedStatuses.includes(phase)) return;

            const id =
                (inst as any)?.ProviderSubjectNo ??
                (inst as any)?.ProviderSubjectNumber ??
                (inst as any)?.['-ProviderSubjectNo'] ??
                (inst as any)?.['-ProviderSubjectNumber'] ??
                (inst as any)?.PROVIDERSUBJECTNO;
            if (id != null) set.add(String(id));
        });
        return set;
    }, [generated?.NonInstallments, includedStatuses]);

    // Filtered installments array (for count and balance sum)
    const filteredInstallments = useMemo(() => {
        if (!generated?.Installments) return [];
        return generated.Installments.filter(inst => {
            const phase =
                (inst as any)?.ContractPhase ??
                (inst as any)?.contractPhase ??
                (inst as any)?.['-ContractPhase'] ??
                (inst as any)?.CONTRACTPHASE;
            if (!phase) return true;
            return includedStatuses.includes(phase);
        });
    }, [generated?.Installments, includedStatuses]);

    // Filtered non-installments array (for count and balance sum)
    const filteredNonInstallments = useMemo(() => {
        if (!generated?.NonInstallments) return [];
        return generated.NonInstallments.filter(inst => {
            const phase =
                (inst as any)?.ContractPhase ??
                (inst as any)?.contractPhase ??
                (inst as any)?.['-ContractPhase'] ??
                (inst as any)?.CONTRACTPHASE;
            if (!phase) return true;
            return includedStatuses.includes(phase);
        });
    }, [generated?.NonInstallments, includedStatuses]);

    // Build a lookup map for installments by all possible IDs (for O(1) lookup)
    const installmentLookup = useMemo(() => {
        const installments = generated?.Installments ?? []
        const lookup = new Map<string, { phase: string; installment: any }>()
        for (const inst of installments) {
            const ids = [
                (inst as any)?.ProviderContractNo,
                (inst as any)?.ProviderSubjectNo,
                (inst as any)?.ProviderSubjectNumber,
                (inst as any)?.ContractNumber,
                (inst as any)?.['-ProviderContractNo'],
                (inst as any)?.['-ProviderSubjectNo'],
                (inst as any)?.['-ProviderSubjectNumber'],
                (inst as any)?.['-ContractNumber'],
            ]
            const phase =
                (inst as any)?.ContractPhase ??
                (inst as any)?.contractPhase ??
                (inst as any)?.['-ContractPhase'] ??
                (inst as any)?.CONTRACTPHASE
            for (const id of ids) {
                if (id != null && !lookup.has(String(id))) {
                    lookup.set(String(id), { phase, installment: inst })
                }
            }
        }
        return lookup
    }, [generated?.Installments])

    // Build a lookup map for non-installments by all possible IDs (for O(1) lookup)
    const nonInstallmentLookup = useMemo(() => {
        const nonInstallments = generated?.NonInstallments ?? []
        const lookup = new Map<string, { phase: string; nonInstallment: any }>()
        for (const inst of nonInstallments) {
            const ids = [
                (inst as any)?.ProviderContractNo,
                (inst as any)?.ProviderSubjectNo,
                (inst as any)?.ProviderSubjectNumber,
                (inst as any)?.ContractNumber,
                (inst as any)?.['-ProviderContractNo'],
                (inst as any)?.['-ProviderSubjectNo'],
                (inst as any)?.['-ProviderSubjectNumber'],
                (inst as any)?.['-ContractNumber'],
            ]
            const phase =
                (inst as any)?.ContractPhase ??
                (inst as any)?.contractPhase ??
                (inst as any)?.['-ContractPhase'] ??
                (inst as any)?.CONTRACTPHASE
            for (const id of ids) {
                if (id != null && !lookup.has(String(id))) {
                    lookup.set(String(id), { phase, nonInstallment: inst })
                }
            }
        }
        return lookup
    }, [generated?.NonInstallments])

    // Validation results after applying contract status/provider filters
    const validationAfterContractFilters = useMemo(() => {
        return validationResults.filter(result => {
            const type = (result.recordType ?? "Individuals") as ValidationRecordType

            // Installment results: lookup by clientId in the pre-built map
            if (type === "Installments") {
                const cid = String(result.clientId ?? "")
                const lookupEntry = installmentLookup.get(cid)
                if (!lookupEntry) return true // No match found, include by default
                const { phase } = lookupEntry
                if (!phase) return true
                return includedStatuses.includes(phase)
            }

            // NonInstallment results: lookup by clientId in the pre-built map
            if (type === "NonInstallments") {
                const cid = String(result.clientId ?? "")
                const lookupEntry = nonInstallmentLookup.get(cid)
                if (!lookupEntry) return true // No match found, include by default
                const { phase } = lookupEntry
                if (!phase) return true
                return includedStatuses.includes(phase)
            }

            // Individuals / Companies: if there are installments present we only include those with a matching provider
            if (installmentLookup.size === 0 && nonInstallmentLookup.size === 0) return true
            return filteredInstallmentProviders.has(String(result.clientId ?? "")) || filteredNonInstallmentProviders.has(String(result.clientId ?? ""))
        })
    }, [validationResults, installmentLookup, nonInstallmentLookup, includedStatuses, filteredInstallmentProviders, filteredNonInstallmentProviders])

    const validationCounts = useMemo(() => {
        const base: Record<ValidationRecordType, number> = {
            Individuals: 0,
            Companies: 0,
            Installments: 0,
            NonInstallments: 0,
        }
        validationAfterContractFilters.forEach(result => {
            const type = (result.recordType ?? "Individuals") as ValidationRecordType
            base[type] = (base[type] ?? 0) + 1
        })
        return base
    }, [validationAfterContractFilters])

    // Filtered validation results based on selected filter
    const filteredValidationResults = useMemo(() => {
        if (validationFilter === "All") {
            return validationAfterContractFilters
        }
        return validationAfterContractFilters.filter(result => (result.recordType ?? "Individuals") === validationFilter)
    }, [validationAfterContractFilters, validationFilter])

    const validationGroups = useMemo<ValidationGroup[]>(() => {
        const map = new Map<string, { group: ValidationGroup; keys: Set<string> }>()
        filteredValidationResults.forEach(result => {
            const key = result.clientId || "(Unknown Client)"
            if (!map.has(key)) {
                map.set(key, {
                    group: {
                        clientId: key,
                        clientName: result.clientName || "",
                        issues: [],
                    },
                    keys: new Set<string>(),
                })
            }
            const entry = map.get(key)!
            const issueKey = [result.recordType ?? "", result.field ?? "", result.reason ?? ""].join("|")
            if (!entry.keys.has(issueKey)) {
                entry.keys.add(issueKey)
                entry.group.issues.push(result)
            }
        })
        return Array.from(map.values()).map(entry => entry.group)
    }, [filteredValidationResults])

    const validationCount = validationAfterContractFilters.length
    const isValidationTab = selected === "Validation"
    const totalValidationPages = Math.ceil(validationGroups.length / validationPageSize)
    const validationCurrent = Math.min(Math.max(validationPage, 0), Math.max(totalValidationPages - 1, 0))
    const validationStartIndex = validationCurrent * validationPageSize
    const validationPageItems = validationGroups.slice(validationStartIndex, validationStartIndex + validationPageSize)
    const validationLabel = isLoadingMeta ? "Validation (...)" : `Validation (${validationCount})`

    const confirmEncryptedDownload = (proceed: () => void) => {
        if (validationCount === 0) {
            proceed()
            return
        }

        refreshDialog({
            id: "download-validation-warning",
            title: "Validation Issues Detected",
            content: `There are ${validationCount} validation result${validationCount === 1 ? "" : "s"}. Downloading the encrypted file may include invalid data. Continue?`,
            confirmContent: "Continue",
            confirmActive: true,
            visible: true,
            onConfirm: () => proceed(),
        }, true)
    }

    const onDownload = (raw:boolean, format?:string) => {
        if (!raw) {
            confirmEncryptedDownload(() => {
                showFilenameDialog(
                    ({ filename, submissionType: selectedType, submissionAttempt: dialogSubmissionAttempt }) => {
                        setSubmissionType(selectedType)
                        const p = performDownload(false, filename, format, selectedType, filename)
                        const buildProoflistXlsxName = () => buildProoflistFilename(generated?.Header ?? {}, dialogSubmissionAttempt, selectedType);
                        p.finally(() => {performDownload(true, buildProoflistXlsxName(), "prooflist", selectedType, filename) })
                    },
                    { initialSubmissionType: submissionType }
                )
            })
        } else {
            const defaultName = (() => {
                if (format === 'csv') {
                    return "generated_csv.zip";
                }
                if (format === 'prooflist') {
                    return buildProoflistFilename(generated?.Header ?? {}, undefined, submissionType);
                }
                if (format === 'validation') {
                    return "generated_validation.zip";
                }
                return "generated_xml.zip";
            })();
            performDownload(true, defaultName, format, submissionType);
        }
    }
    
    const performDownload = (raw: boolean, filename: string, format?: string, selectedType?: number, previewFilename?: string) => {
        const effectiveSubmissionType = selectedType ?? submissionType
        setDownloading(true);
        return downloadGenerated(raw, filename, generated.Filename, format, effectiveSubmissionType, { excludeContractStatuses: excludedStatuses }, previewFilename)
        .then(result => {
            if (result.code === "200") {
                const fileType = format === 'csv' ? 'CSV' : format === 'prooflist' ? 'Prooflist Excel' : format === 'validation' ? 'Validation CSV' : 'XML';
                if (!raw) {
                    addToast("Download Complete", `Encrypted ${fileType} file downloaded as: ${filename}`, null, "success");
                } else {
                    addToast("Download Complete", `Raw ${fileType} file downloaded as: ${filename}`, null, "success");
                }
            } else {
                const errorMessage = result.message || "Please check your settings.";
                addToast("Download Failed", errorMessage, null, "danger");
            }
        })
        .finally(() => setDownloading(false));
    }

    const onDiscard = (dirty:boolean) => {
        discardUnsaved()
        if (!dirty) {
            unfocus()
            return
        } 
        addToast("Discarded", "Unsaved data has been discarded.", null, "warning")
    }

    const onSave = async () => {
        addToast("Saving...", "Saving generated data to library.", null, "info")
        const result = await saveGenerated();
        if (result === "200") {
            addToast("Saved Successfully", "Data saved to XML library.", null, "success")
        } else {
            addToast("Save Failed", `Could not save data (Code: ${result}).`, null, "danger")
        }
    }

    const onClickPage = (page:number) => {
        setActive(page)
    }

    useEffect(() => {
        const maxPage = Math.max(totalValidationPages - 1, 0)
        if (validationPage > maxPage) {
            setValidationPage(maxPage)
        }
    }, [validationPage, totalValidationPages])

    let selectedData = generated.Individuals
    switch (selected) {
        case "Companies":
            selectedData = generated.Companies
            break
        case "Installments":
            selectedData = generated.Installments
            break
        case "NonInstallments":
            selectedData = generated.NonInstallments
            break
        default:
            break
    }
    if (!selectedData) selectedData = []

    const filteredData = selectedData;

    // Count of Individuals 
    const filteredIndividualsCount = useMemo(() => {
        if (!generated?.Individuals) return 0;
        if ((!generated?.Installments || generated.Installments.length === 0) && (!generated?.NonInstallments || generated.NonInstallments.length === 0)) return generated.Individuals.length;

        return generated.Individuals.filter(ind => {
            const id =
                (ind as any)?.ProviderSubjectNumber ??
                (ind as any)?.ProviderSubjectNo ??
                (ind as any)?.['-ProviderSubjectNumber'] ??
                (ind as any)?.['-ProviderSubjectNo'] ??
                (ind as any)?.PROVIDERSUBJECTNUMBER;
            return id != null && (filteredInstallmentProviders.has(String(id)) || filteredNonInstallmentProviders.has(String(id)));
        }).length;
    }, [generated?.Individuals, filteredInstallmentProviders, filteredNonInstallmentProviders, generated?.Installments, generated?.NonInstallments]);

    // Count of Companies 
    const filteredCompaniesCount = useMemo(() => {
        if (!generated?.Companies) return 0;
        if ((!generated?.Installments || generated.Installments.length === 0) && (!generated?.NonInstallments || generated.NonInstallments.length === 0)) return generated.Companies.length;

        return generated.Companies.filter(comp => {
            const id =
                (comp as any)?.ProviderSubjectNumber ??
                (comp as any)?.ProviderSubjectNo ??
                (comp as any)?.['-ProviderSubjectNumber'] ??
                (comp as any)?.['-ProviderSubjectNo'] ??
                (comp as any)?.PROVIDERSUBJECTNUMBER;
            return id != null && (filteredInstallmentProviders.has(String(id)) || filteredNonInstallmentProviders.has(String(id)));
        }).length;
    }, [generated?.Companies, filteredInstallmentProviders, filteredNonInstallmentProviders, generated?.Installments, generated?.NonInstallments]);

    const totalPages = Math.ceil(filteredData.length / countsPerPage)
    const tableCurrent = Math.min(Math.max(active, 0), Math.max(totalPages - 1, 0))
    const tableStartIndex = tableCurrent * countsPerPage
    const displayableItems = isValidationTab ? [] : filteredData.slice(tableStartIndex, tableStartIndex + countsPerPage)

    const renderPaginationControls = (current: number, total: number, onChange: (page: number) => void) => {
        if (total <= 1) {
            return null
        }

        const maxVisible = 7
        const sidePages = 2
        const safeTotal = Math.max(total, 0)
        const safeCurrent = Math.min(Math.max(current, 0), Math.max(safeTotal - 1, 0))
        const items: React.ReactNode[] = []

        const go = (target: number) => {
            const clamped = Math.min(Math.max(target, 0), Math.max(safeTotal - 1, 0))
            onChange(clamped)
        }

        const pushPage = (pageNumber: number) => {
            items.push(
                <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === safeCurrent}
                    onClick={() => go(pageNumber)}
                >
                    {pageNumber + 1}
                </Pagination.Item>
            )
        }

        pushPage(0)

        if (safeTotal <= maxVisible) {
            for (let i = 1; i < safeTotal; i++) {
                pushPage(i)
            }
        } else {
            let start = Math.max(1, safeCurrent - sidePages)
            let end = Math.min(safeTotal - 2, safeCurrent + sidePages)

            if (safeCurrent < maxVisible - 3) {
                end = Math.min(safeTotal - 2, maxVisible - 2)
            } else if (safeCurrent > safeTotal - maxVisible + 2) {
                start = Math.max(1, safeTotal - maxVisible + 1)
            }

            if (start > 1) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />)
            }

            for (let i = start; i <= end; i++) {
                pushPage(i)
            }

            if (end < safeTotal - 2) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />)
            }

            pushPage(safeTotal - 1)
        }

        return (
            <div className="pagination-controls" style={{display:"inline-block"}}>
                <Stack style={{margin:"auto"}} direction="horizontal" gap={2}>
                    <p>Pages</p>
                    <Pagination size="sm">
                        <Pagination.First onClick={() => go(0)} disabled={safeCurrent === 0} />
                        <Pagination.Prev onClick={() => go(safeCurrent - 1)} disabled={safeCurrent === 0} />
                        {items}
                        <Pagination.Next onClick={() => go(safeCurrent + 1)} disabled={safeCurrent === safeTotal - 1} />
                        <Pagination.Last onClick={() => go(safeTotal - 1)} disabled={safeCurrent === safeTotal - 1} />
                    </Pagination>
                </Stack>
            </div>
        )
    }

    const paginationContent = isValidationTab
        ? renderPaginationControls(validationCurrent, totalValidationPages, setValidationPage)
        : renderPaginationControls(tableCurrent, totalPages, onClickPage)

    const statusFilterControls = (
        <Stack direction="horizontal" gap={3} className="flex-wrap align-items-center status-filter-controls" style={{ paddingTop: 8, paddingBottom: 8, paddingLeft: 8, paddingRight: 8 }}>
            <span className="text-muted">Filter Contract:</span>
            {CONTRACT_STATUS_FILTERS.map(option => (
                <label key={option.value} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, cursor: "pointer" }}>
                    <input
                        type="checkbox"
                        checked={includedStatuses.includes(option.value)}
                        onChange={() => toggleStatusInclusion(option.value)}
                        style={{ margin: 0 }}
                    />
                    <span>{option.label}</span>
                </label>
            ))}
        </Stack>
    )

    // Validation filter controls (dropdown to select record type)
    const validationFilterControls = (
        <Stack direction="horizontal" gap={2} className="flex-wrap align-items-center status-filter-controls" style={{ paddingTop: 8, paddingBottom: 8 }}>
            <span className="text-muted">Show Validation:</span>
            <Form.Select
                size="sm"
                value={validationFilter}
                style={{ maxWidth: 240 }}
                onChange={event => {
                    const next = event.target.value as "All" | ValidationRecordType
                    setValidationFilter(next)
                    setValidationPage(0)
                }}
            >
                <option value="All">{`All Records (${validationCount})`}</option>
                <option value="Individuals">{`Individuals (${validationCounts.Individuals})`}</option>
                <option value="Companies">{`Companies (${validationCounts.Companies})`}</option>
                <option value="Installments">{`Installments (${validationCounts.Installments})`}</option>
                <option value="NonInstallments">{`NonInstallments (${validationCounts.NonInstallments})`}</option>
            </Form.Select>
        </Stack>
    )

    const formatNumber = (value: number) =>
        new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);

    const installmentBalanceSum = useMemo(() => {
        if (selected !== "Installments" || isValidationTab) {
            return null;
        }
        return filteredInstallments.reduce((acc, installment) => {
            const raw =
                (installment as any)?.['-OutstandingBalance'] ??
                (installment as any)?.OutstandingBalance ??
                (installment as any)?.outstandingBalance ??
                (installment as any)?.OUTSTANDINGBALANCE;
            if (raw == null) return acc;
            const num = typeof raw === "number" ? raw : Number(String(raw).replace(/,/g, ""));

            return acc + (isNaN(num) ? 0 : num);
        }, 0);
    }, [filteredInstallments, selected, isValidationTab]);

    const nonInstallmentBalanceSum = useMemo(() => {
        if (selected !== "NonInstallments" || isValidationTab) {
            return null;
        }
        return filteredNonInstallments.reduce((acc, nonInstallment) => {
            const raw =
                (nonInstallment as any)?.['-UtilizationOutstandingBalance'] ??
                (nonInstallment as any)?.UtilizationOutstandingBalance ??
                (nonInstallment as any)?.utilizationOutstandingBalance ??
                (nonInstallment as any)?.UTILIZATIONOUTSTANDINGBALANCE;
            if (raw == null) return acc;
            const num = typeof raw === "number" ? raw : Number(String(raw).replace(/,/g, ""));

            return acc + (isNaN(num) ? 0 : num);
        }, 0);
    }, [filteredNonInstallments, selected, isValidationTab]);

    const summarySection = useMemo(() => {
        const items: string[] = [];
        if (selected === "Individuals") {
            items.push(`Total Individuals: ${filteredIndividualsCount}`);
        } else if (selected === "Companies") {
            items.push(`Total Companies: ${filteredCompaniesCount}`);
        } else if (selected === "Installments") {
            items.push(`Total Installments: ${filteredInstallments.length}`);
        } else if (selected === "NonInstallments") {
            items.push(`Total NonInstallments: ${filteredNonInstallments.length}`);
        }
        if (installmentBalanceSum != null) {
            items.push(`Outstanding Balance Total: ${formatNumber(installmentBalanceSum)}`);
        }
        if (nonInstallmentBalanceSum != null) {
            items.push(`Outstanding Balance Total: ${formatNumber(nonInstallmentBalanceSum)}`);
        }

        return (
            <div className="summary-row">
                <div className="summary-text" title={items.length > 0 ? items.join(" | ") : ""}>
                    {items.join(" | ")}
                </div>
                <div className="summary-filter-block">
                    {statusFilterControls}
                </div>
            </div>
        );
    }, [filteredInstallments.length, filteredNonInstallments.length, selected, isValidationTab, installmentBalanceSum, nonInstallmentBalanceSum, statusFilterControls, filteredIndividualsCount, filteredCompaniesCount]);
    const paginationSection = paginationContent ? <div className="pagination-wrapper">{paginationContent}</div> : null;

    const handleTabSelect = (key: string | null) => {
        if (!key) return
        setSelected(key)
        setActive(0)
        if (key === "Validation") {
            setValidationPage(0)
        }
    }

    const renderTabLabel = (key: string, label: React.ReactNode) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ display: "inline-flex", alignItems: "center" }}>
                {TAB_ICONS[key] ?? null}
            </span>
            <span>{label}</span>
        </span>
    )

    return <BasePage>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <Label>{"Cutoff " + cutoff}</Label>
            <Nav variant="tabs" activeKey={selected} onSelect={handleTabSelect}>
                <Nav.Item>
                    <Nav.Link eventKey="Individuals">
                        {renderTabLabel("Individuals", "Individuals")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="Companies">
                        {renderTabLabel("Companies", "Companies")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="Installments">
                        {renderTabLabel("Installments", "Installments")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="NonInstallments">
                        {renderTabLabel("NonInstallments", "NonInstallments")}
                    </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="Validation">
                        {renderTabLabel("Validation", validationLabel)}
                    </Nav.Link>
                </Nav.Item>
            </Nav>
        </div>
        {/* check if validation tab is selected, then show validation filter controls */}
        {isValidationTab && (
            <div style={{ marginTop: 12 }}>
                {validationFilterControls}
            </div>
        )}
        {isValidationTab
            ? <ValidationPanel groups={validationPageItems} isLoading={isLoadingMeta}/>
            : <DataTable generated={displayableItems}/>}        
        
        <ProcessBar processing={false} 
            preparing={downloading} 
            dirty={status === STATUS_UNSAVED}
            saving={isSaving}
            savingState={status === STATUS_SAVING}
            onDownload={onDownload}
            onSave={onSave}
            onDiscard={onDiscard}
            footer={paginationSection}>
                {summarySection}
        </ProcessBar>
    </BasePage>
}

export default DataViewPage

